import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import announcementService, { AnnouncementPopup } from '../../services/announcementService';
import AnnouncementModal from './AnnouncementModal';

/**
 * AnnouncementProvider
 *
 * Wraps the authenticated app to fetch and display popup announcements.
 * Fetches pending popups on mount (when user is authenticated) and shows
 * them one at a time as centered modal dialogs.
 *
 * Place this inside AuthProvider and router context.
 */

interface AnnouncementProviderProps {
  children: React.ReactNode;
}

const AnnouncementProvider: React.FC<AnnouncementProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [, setQueue] = useState<AnnouncementPopup[]>([]);
  const [currentPopup, setCurrentPopup] = useState<AnnouncementPopup | null>(null);
  const hasFetched = useRef(false);

  // Fetch pending popups when user is authenticated
  useEffect(() => {
    if (!isAuthenticated || !user || !user.onboardingCompleted || hasFetched.current) return;

    const fetchPopups = async () => {
      try {
        const popups = await announcementService.getPendingPopups();
        if (popups.length > 0) {
          // Sort by priority (higher first)
          const sorted = popups.sort((a, b) => (b.priority || 0) - (a.priority || 0));
          setQueue(sorted);
          setCurrentPopup(sorted[0]);

          // Mark first one as displayed
          try {
            await announcementService.markDisplayed(sorted[0].messageId);
          } catch {
            // Non-critical, continue showing the popup
          }
        }
      } catch (error) {
        // Silently fail - announcements are non-critical
      }
      hasFetched.current = true;
    };

    // Small delay to not block initial page load
    const timer = setTimeout(fetchPopups, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user]);

  const showNextPopup = useCallback(() => {
    setQueue(prev => {
      const remaining = prev.slice(1);
      if (remaining.length > 0) {
        setCurrentPopup(remaining[0]);
        // Mark as displayed
        announcementService.markDisplayed(remaining[0].messageId).catch(() => {});
      } else {
        setCurrentPopup(null);
      }
      return remaining;
    });
  }, []);

  const handleDismiss = useCallback(async () => {
    if (!currentPopup) return;
    try {
      await announcementService.markDismissed(currentPopup.messageId);
    } catch {
      // Non-critical
    }
    showNextPopup();
  }, [currentPopup, showNextPopup]);

  const handleCtaClick = useCallback(async () => {
    if (!currentPopup) return;
    try {
      await announcementService.markClicked(currentPopup.messageId);
    } catch {
      // Non-critical
    }

    const ctaUrl = currentPopup.popupConfig?.ctaUrl;
    if (ctaUrl) {
      // Internal navigation for relative URLs, external for absolute
      if (ctaUrl.startsWith('/')) {
        navigate(ctaUrl);
      } else if (ctaUrl.startsWith('http')) {
        window.open(ctaUrl, '_blank');
      }
    }
    showNextPopup();
  }, [currentPopup, showNextPopup, navigate]);

  const handlePageCtaClick = useCallback(async (url: string) => {
    if (!currentPopup) return;
    try {
      await announcementService.markClicked(currentPopup.messageId);
    } catch {
      // Non-critical
    }
    if (url.startsWith('/')) {
      navigate(url);
    } else if (url.startsWith('http')) {
      window.open(url, '_blank');
    }
    showNextPopup();
  }, [currentPopup, showNextPopup, navigate]);

  return (
    <>
      {children}
      {currentPopup && (
        <AnnouncementModal
          announcement={currentPopup}
          onDismiss={handleDismiss}
          onCtaClick={handleCtaClick}
          onPageCtaClick={handlePageCtaClick}
        />
      )}
    </>
  );
};

export default AnnouncementProvider;
